import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Check, Copy, ListFilter, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { useLocation } from "wouter";
import { useRoute } from "wouter";
import CommonGrid from "@/components/pos/admin/common/grid";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import AdvanceFiltersModal, { type FilterConfig } from "@/components/pos/admin/common/advance-filters-modal";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import { useDebounce } from "@/hooks/pos/use-debounce";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { NameAvatar } from "@/components/pos/admin/common/avatar";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { useSechPrice } from "@/hooks/pos/use-sech-price";
import { posApiFetch } from "@/utils/pos-api";
import { getStatusColorCode, getStatusDotColor } from "@/lib/pos/global";
import type { Location as PosLocation } from "@/types/pos";
import { useToast } from "@/hooks/use-toast";
import ProductEditor from "@/components/pos/admin/product-editor";

const fetchProducts = async () => {
  const res = await posApiFetch("/v1/pos/products");
  if (!res.ok) throw new Error("Failed to fetch products");
  return res.json();
};

const fetchLocations = async () => {
  const res = await posApiFetch("/v1/pos/locations");
  if (!res.ok) throw new Error("Failed to fetch locations");
  return res.json();
};

type ProductsState = {
  availability?: string;
  category?: string;
  subcategory?: string;
  dishType?: string;
  currentPage?: number;
  itemsPerPage?: number;
  searchQuery?: string;
  dateRange?: { from: string; to: string };
  priceRange?: { min: string; max: string };
};

const defaultProductsState: ProductsState = {
  availability: "all",
  category: "all",
  subcategory: "all",
  dishType: "all",
  currentPage: 1,
  itemsPerPage: 10,
  priceRange: { min: "", max: "" },
};

const normalizeDate = (value: any): Date | null => {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    if (!value.trim()) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

export default function ProductsPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const { sechPrice } = useSechPrice();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const [isAddRoute] = useRoute("/admin/products/add");
  const [isEditRoute, editParams] = useRoute("/admin/products/:id/edit");

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  useEffect(() => { document.title = "Products | SECH Admin"; }, []);

  const [productsState, setProductsState] = usePagePersistentState<ProductsState>("products", defaultProductsState);
  const searchQuery = productsState.searchQuery;
  const currentPage = productsState.currentPage;
  const itemsPerPage = productsState.itemsPerPage;
  const availabilityFilter = productsState.availability;
  const categoryFilter = productsState.category;
  const subcategoryFilter = productsState.subcategory;
  const dishTypeFilter = productsState.dishType;
  const priceRange = productsState.priceRange;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "products_table",
    defaultColumn: "name",
    defaultDirection: "asc",
  });

  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    availability: "", category: "", subcategory: "", dishType: "", dateRange: { from: "", to: "" }, priceRange: { min: "", max: "" },
  });

  useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      availability: availabilityFilter ?? "",
      category: categoryFilter ?? "",
      subcategory: subcategoryFilter ?? "",
      dishType: dishTypeFilter ?? "",
      dateRange: { from: productsState.dateRange?.from ?? "", to: productsState.dateRange?.to ?? "" },
      priceRange: { min: priceRange?.min ?? "", max: priceRange?.max ?? "" },
    }));
  }, [availabilityFilter, categoryFilter, subcategoryFilter, dishTypeFilter, productsState.dateRange, priceRange]);

  const handleApplyFilters = (values: Record<string, any>) => {
    const fromDate = normalizeDate(values.dateRange?.from);
    const toDate = normalizeDate(values.dateRange?.to);
    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      toast({ title: "Invalid filters", description: '"From" date cannot be after "To" date', variant: "destructive" });
      return;
    }
    setFilterValues(values);
    setProductsState((prev) => ({
      ...prev,
      availability: values.availability || "all",
      category: values.category || "all",
      subcategory: values.subcategory || "all",
      dishType: values.dishType || "all",
      currentPage: 1,
      dateRange: { from: values.dateRange?.from ?? "", to: values.dateRange?.to ?? "" },
      priceRange: { min: values.priceRange?.min ? String(values.priceRange.min) : "", max: values.priceRange?.max ? String(values.priceRange.max) : "" },
    }));
  };

  const { data: products = [], isLoading, error } = useQuery({
    queryKey: ["/v1/pos/products"],
    queryFn: fetchProducts,
    retry: 3,
    retryDelay: 1000,
  });

  const { data: locations = [] } = useQuery({
    queryKey: ["/v1/pos/locations"],
    queryFn: fetchLocations,
    retry: 3,
    retryDelay: 1000,
  });

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await posApiFetch(`/v1/pos/products/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete product");
      toast({ title: "Product deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/products"] });
    } catch {
      toast({ title: "Error", description: "Failed to delete product", variant: "destructive" });
    } finally {
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleDuplicate = async (row: any) => {
    try {
      const res = await posApiFetch(`/v1/pos/products/${row.id}/duplicate`, { method: "POST" });
      if (!res.ok) throw new Error("Failed to duplicate product");
      toast({ title: "Product duplicated successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/products"] });
    } catch {
      toast({ title: "Error", description: "Failed to duplicate product", variant: "destructive" });
    }
  };

  const getFilterCount = () => {
    let count = 0;
    if (productsState.availability && productsState.availability !== "all") count += 1;
    if (productsState.category && productsState.category !== "all") count += 1;
    if (productsState.subcategory && productsState.subcategory !== "all") count += 1;
    if (productsState.dishType && productsState.dishType !== "all") count += 1;
    const fromDate = normalizeDate(productsState.dateRange?.from);
    const toDate = normalizeDate(productsState.dateRange?.to);
    if (fromDate || toDate) count += 1;
    const minStr = productsState.priceRange?.min?.toString()?.trim() ?? "";
    const maxStr = productsState.priceRange?.max?.toString()?.trim() ?? "";
    if (minStr !== "" || maxStr !== "") count += 1;
    return count;
  };

  const availabilityOptions = [
    { label: "All", value: "all" },
    { label: "Available", value: "true" },
    { label: "Unavailable", value: "false" },
  ];

  const categoryOptions = useMemo(() => {
    const cats = [...new Set(products.map((p: any) => p.category).filter(Boolean))] as string[];
    return [{ label: "All", value: "all" }, ...cats.map((c) => ({ label: c.charAt(0).toUpperCase() + c.slice(1), value: c }))];
  }, [products]);

  const subcategoryOptions = useMemo(() => {
    const subs = [...new Set(products.map((p: any) => p.subcategory?.name || p.subcategoryName).filter(Boolean))] as string[];
    return [{ label: "All", value: "all" }, ...subs.map((s) => ({ label: s.charAt(0).toUpperCase() + s.slice(1), value: s }))];
  }, [products]);

  const dishTypeOptions = useMemo(() => {
    const types: string[] = [];
    products.forEach((p: any) => {
      if (p.isSignature) types.push("signature");
      if (p.isSpicy) types.push("spicy");
      if (p.isVegan) types.push("vegan");
      if (p.isRaw) types.push("raw");
    });
    const unique = [...new Set(types)];
    return [{ label: "All", value: "all" }, ...unique.map((t) => ({ label: t.charAt(0).toUpperCase() + t.slice(1), value: t }))];
  }, [products]);

  const productFilters: FilterConfig[] = useMemo(() => [
    { key: "availability", label: "Availability", type: "select", options: availabilityOptions, placeholder: "Select Option" },
    { key: "category", label: "Category", type: "select", options: categoryOptions, placeholder: "Select Option" },
    { key: "subcategory", label: "Sub Category", type: "select", options: subcategoryOptions, placeholder: "Select Option" },
    { key: "dishType", label: "Dish Type", type: "select", options: dishTypeOptions, placeholder: "Select Option" },
    { key: "priceRange", label: "Price Range (USD)", type: "numberRange", min: 0, max: 200, step: 1, prefix: "$" },
  ], [categoryOptions, subcategoryOptions, dishTypeOptions]);

  const handleClear = () => {
    setFilterValues({ availability: "all", category: "all", subcategory: "all", dishType: "all", dateRange: { from: "", to: "" }, priceRange: { min: "", max: "" } });
    setProductsState((prev) => ({ ...prev, availability: "all", category: "all", subcategory: "all", dishType: "all", dateRange: { from: "", to: "" }, currentPage: 1, priceRange: { min: "", max: "" } }));
  };

  const toggleRow = (id: number) => {
    setSelectedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      setSelectedRows(new Set(products.map((p: any) => p.id)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const setTableColumns = useMemo(() => [
    {
      label: "", field: "_select", sortable: false,
      headerTemplate: () => (
        <Checkbox
          checked={selectedRows.size > 0 && selectedRows.size === products.length}
          onCheckedChange={(checked) => toggleAll(!!checked)}
          className="h-4 w-4"
        />
      ),
      cellTemplate: (_value: any, row: any) => (
        <Checkbox
          checked={selectedRows.has(row.id)}
          onCheckedChange={() => toggleRow(row.id)}
          className="h-4 w-4"
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      ),
    },
    {
      label: "Name", field: "name", sortable: true,
      cellTemplate: (value: string, row: any) => (
        <div className="flex items-center gap-2">
          <NameAvatar name={value || ""} imageUrl={row?.imageUrl} />
          <span className="text-[13px] text-[#505258]">{value}</span>
        </div>
      ),
    },
    {
      label: "Menus", field: "menuTypes",
      cellTemplate: (value: any) => {
        if (!value || !Array.isArray(value) || value.length === 0) return <span className="text-[13px] text-[var(--secondary-text-color)]">-</span>;
        return <span className="text-[13px] text-[var(--secondary-text-color)]">{value.map((m: any) => m.name || m).join(", ")}</span>;
      },
    },
    {
      label: "Category", field: "category", sortable: true,
      cellTemplate: (value: string) => <span className="text-[13px] text-[var(--secondary-text-color)]">{value ? value.charAt(0).toUpperCase() + value.slice(1) : "-"}</span>,
    },
    {
      label: "Sub Category", field: "subcategory",
      cellTemplate: (value: any) => {
        const name = typeof value === "object" ? value?.name : value;
        return <span className="text-[13px] text-[var(--secondary-text-color)]">{name ? String(name).charAt(0).toUpperCase() + String(name).slice(1) : "-"}</span>;
      },
    },
    {
      label: "Price (USD)", field: "priceUsd", sortable: true,
      cellTemplate: (value: any) => {
        const usd = parseFloat(value ?? "0");
        const sech = sechPrice > 0 ? (usd / sechPrice).toFixed(2) : "—";
        return <span className="text-[13px] text-[var(--secondary-text-color)]">${usd.toFixed(2)} / {sech} SECH</span>;
      },
    },
    {
      label: "Locations", field: "locations",
      cellTemplate: (value: any) => {
        if (!value || !Array.isArray(value) || value.length === 0) return <span className="text-[13px] text-[var(--secondary-text-color)]">-</span>;
        const [first, ...rest] = value;
        return (
          <div className="flex items-center gap-1">
            <span className="h-[18px] w-[18px] rounded-[4px]" style={{ backgroundColor: first?.colorCode }} />
            <span className="text-[13px] text-[var(--secondary-text-color)]">{first?.name}</span>
            {rest.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="h-[22px] w-[22px] rounded-full bg-[var(--secondary-background-color)] text-[11px] text-[#53585B] font-medium flex items-center justify-center hover:bg-[var(--primary-background-hover-color)]">+{rest.length}</button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-[160px] p-0 rounded-[6px]">
                  {rest.map((loc: any, i: number) => (
                    <DropdownMenuItem key={i} className="flex items-center gap-2 border-b border-[#E4E5EB] text-[13px]">
                      <span className="h-[14px] w-[14px] rounded-[3px]" style={{ backgroundColor: loc?.colorCode }} />
                      <span className="text-[12px] text-[#53585B]">{loc?.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        );
      },
    },
    {
      label: "Availability", field: "available", sortable: true,
      cellTemplate: (value: any) => value ? <Check className="h-4 w-4 text-green-600" /> : <X className="h-4 w-4 text-red-500" />,
    },
    {
      label: "Dish Type", field: "dishType",
      cellTemplate: (_value: any, row: any) => {
        const tags: string[] = [];
        if (row.isSignature) tags.push("Signature");
        if (row.isSpicy) tags.push("Spicy");
        if (row.isVegan) tags.push("Vegan");
        if (row.isRaw) tags.push("Raw");
        return <span className="text-[13px] text-[var(--secondary-text-color)]">{tags.length > 0 ? tags.join(", ") : "-"}</span>;
      },
    },
    {
      label: "Modifiers", field: "modifierGroups",
      cellTemplate: (value: any) => <span className="text-[13px] text-[var(--secondary-text-color)]">{Array.isArray(value) ? value.length : 0}</span>,
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: any, row: any) => (
        <ActionTemplate
          row={row}
          actions={[
            { label: "Edit", icon: Pencil, onClick: (r: any) => navigate(`/admin/products/${r.id}/edit`) },
            { label: "Duplicate", icon: Copy, onClick: (r: any) => handleDuplicate(r) },
            { label: "Delete", icon: Trash2, onClick: (r: any) => { setDeleteTarget(r); setDeleteModal(true); }, destructive: true },
          ]}
        />
      ),
    },
  ], [selectedRows, products, sechPrice]);

  const filteredProducts = useMemo(() =>
    products.filter((product: any) => {
      const search = debouncedSearch?.trim().toLowerCase() ?? "";
      const matchesAvailability = availabilityFilter === "all" || String(product.available) === availabilityFilter;
      const matchesCategory = categoryFilter === "all" || product.category === categoryFilter;
      const subName = typeof product.subcategory === "object" ? product.subcategory?.name : product.subcategoryName;
      const matchesSubcategory = subcategoryFilter === "all" || subName === subcategoryFilter;
      const matchesDishType = dishTypeFilter === "all" || (dishTypeFilter === "signature" && product.isSignature) || (dishTypeFilter === "spicy" && product.isSpicy) || (dishTypeFilter === "vegan" && product.isVegan) || (dishTypeFilter === "raw" && product.isRaw);
      const matchesSearch = !search || product.name?.toLowerCase().includes(search) || product.category?.toLowerCase().includes(search) || subName?.toLowerCase().includes(search);
      const minStr = productsState.priceRange?.min ?? "";
      const maxStr = productsState.priceRange?.max ?? "";
      const min = minStr === "" ? null : Number(minStr);
      const max = maxStr === "" ? null : Number(maxStr);
      const price = parseFloat(product.priceUsd ?? "0");
      const matchesPriceRange = (min === null || price >= min) && (max === null || price <= max);
      return matchesAvailability && matchesCategory && matchesSubcategory && matchesDishType && matchesSearch && matchesPriceRange;
    }), [products, availabilityFilter, categoryFilter, subcategoryFilter, dishTypeFilter, debouncedSearch, productsState.priceRange]
  );

  const sortedProducts = useMemo(() => {
    return [...filteredProducts].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "name": aVal = a?.name ?? ""; bVal = b?.name ?? ""; break;
        case "category": aVal = a?.category ?? ""; bVal = b?.category ?? ""; break;
        case "priceUsd": aVal = parseFloat(a?.priceUsd ?? "0"); bVal = parseFloat(b?.priceUsd ?? "0"); break;
        case "available": aVal = a?.available ? 1 : 0; bVal = b?.available ? 1 : 0; break;
        default: aVal = a?.name ?? ""; bVal = b?.name ?? "";
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredProducts, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedProducts.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedProducts = sortedProducts.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setProductsState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setProductsState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedProducts.length, handlePageChange, handleItemsPerPageChange };

  // Sub-route handling
  if (isAddRoute) {
    return <ProductEditor onBack={() => navigate("/admin/products")} />;
  }

  if (isEditRoute) {
    return <ProductEditor productId={editParams?.id} onBack={() => navigate("/admin/products")} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Products</h2>
          <p className="text-gray-600 mb-4">Failed to load products</p>
          <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Products</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className={`flex h-8 items-center px-1 p-2 rounded-sm ${getFilterCount() > 0 ? "bg-[var(--secondary-background-color)]" : ""}`}>
            <Button onClick={() => setIsFilterOpen(true)} variant="ghost" className={`px-2 text-[var(--primary-text-color)] flex items-center text-[13px] font-medium leading-[15px] justify-center gap-2 rounded-[4px] ${getFilterCount() > 0 ? "h-6 hover:bg-[var(--primary-background-hover-color)]" : "h-8"}`}>
              <ListFilter className="h-4 w-4" /> Filter
              {getFilterCount() > 0 && <span className="text-[13px] bg-[var(--primary-color)] h-4 w-4 flex items-center justify-center rounded-full leading-[15px] font-medium text-white">{getFilterCount()}</span>}
            </Button>
            {getFilterCount() > 0 && (
              <>
                <span className="text-[13px] text-[var(--secondary-text-color)] px-1">|</span>
                <Button type="button" onClick={handleClear} className="text-[13px] text-[var(--primary-text-color)] font-medium px-2 h-6 hover:bg-[var(--primary-background-hover-color)] bg-transparent">Clear</Button>
              </>
            )}
          </div>
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center" type="text" placeholder="Search" value={searchQuery ?? ""} onChange={(e) => setProductsState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))} />
          </div>
          <Button onClick={() => navigate("/admin/products/add")} className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add Product
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        {selectedRows.size > 0 && (
          <div className="mb-3 flex items-center gap-2 text-[13px] text-[var(--secondary-text-color)]">
            <span>{selectedRows.size} selected</span>
            <Button variant="ghost" className="text-[13px] h-7" onClick={() => setSelectedRows(new Set())}>Clear</Button>
          </div>
        )}
        <div className="pt-0 h-[calc(100vh-130px)]">
          {!paginatedProducts.length && !isLoading ? (
            <NoRecordFound title="Products" description="There are no products available." action={
              <Button onClick={() => navigate("/admin/products/add")} className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1">
                <Plus className="h-4 w-4" /> Add Product
              </Button>
            } />
          ) : (
            <CommonGrid
              rowData={paginatedProducts}
              isLoading={isLoading}
              columns={setTableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>
      <AdvanceFiltersModal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter" filters={productFilters} initialValues={filterValues} onApply={handleApplyFilters} onClearAll={handleClear} />
      <ConfirmModal open={deleteModal} onClose={() => { setDeleteModal(false); setDeleteTarget(null); }} onConfirm={handleDelete} title="Delete Product" description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`} confirmText="Delete" variant="delete" />
    </div>
  );
}
